<script lang="ts">
  import BaseStep from "./BaseStep.svelte";
  import SharedHeading from "../SharedHeading.svelte";
  import { getCopy } from "../../lib/stores/copy";
  import Button from "../Button.svelte";
  import { SecondaryAuthFlow } from "../../lib/helpers/auth/SecondaryAuthFlow";
  import { GoogleAuthFlow } from "../../lib/helpers/auth/GoogleAuthFlow";
  import { sessionStore } from "../../lib/stores/session";
  import { settingsStore } from "../../lib/stores/settings";
  import Youtube from "../../assets/icons/youtube.svg";
  import Twitch from "../../assets/icons/twitch.svg";
  import Activision from "../../assets/icons/activision.svg";
  import LinkedIn from "../../assets/icons/linkedin.svg";
  import { onMount } from "svelte";
  import { getSearchParam } from "../../lib/helpers/document";
  import LoadingScreen from "../LoadingScreen.svelte";

  export let currentRoute;

  let busyChannel: string;
  let hasYoutubeError = false;
  let isFinishingAuth = false;

  type ChannelKey = "linked-in" | "twitch" | "youtube" | "callofduty";

  const onYoutubeAuth = async () => {
    busyChannel = "youtube";
    hasYoutubeError = false;

    try {
      await new GoogleAuthFlow().start();
    } catch (e) {
      busyChannel = "";

      if ((e as Error).message === "No YouTube channels found") {
        hasYoutubeError = true;
      }
    }
  };

  const channelMetadata: Record<
    ChannelKey,
    {
      icon: string;
      authFunction: () => Promise<void>;
      buttonCopyKey: string;
    }
  > = {
    "linked-in": {
      icon: LinkedIn,
      authFunction: () => onSecondaryAuth("linked-in"),
      buttonCopyKey: "linkedInStartButtonCopy"
    },
    twitch: {
      icon: Twitch,
      authFunction: () => onSecondaryAuth("twitch"),
      buttonCopyKey: "twitchStartButtonCopy"
    },
    callofduty: {
      icon: Activision,
      authFunction: () => onSecondaryAuth("callofduty"),
      buttonCopyKey: "callOfDutyStartButtonCopy"
    },
    youtube: {
      icon: Youtube,
      authFunction: onYoutubeAuth,
      buttonCopyKey: "youtubeStartButtonCopy"
    }
  };

  onMount(() => {
    busyChannel = "";
    isFinishingAuth = !!getSearchParam("jwt");
    $sessionStore.isAuthenticating = false;
  });

  const onSecondaryAuth = async (channel: string) => {
    try {
      busyChannel = channel;
      await new SecondaryAuthFlow().start(channel);
    } catch (e) {
      $sessionStore.isAuthenticating = false;
    }
  };

  const isValidChannel = (channel: string): channel is ChannelKey => {
    return channel in channelMetadata;
  };

  $: isAuthenticating = $sessionStore.isAuthenticating;
  $: availableChannels = ($settingsStore.secondary_channels ?? []).filter(isValidChannel);
  $: copy = $getCopy("secondaryChannelAuth") as Record<string, string>;
</script>

<svelte:head>
  <script src="https://accounts.google.com/gsi/client" async defer></script>
</svelte:head>

<BaseStep {currentRoute}>
  <svelte:fragment slot="content">
    {#if isFinishingAuth}
      <LoadingScreen />
    {:else}
      <SharedHeading headerCopy={copy.headerCopy} subHeaderCopy={copy.subHeaderCopy} />
      <div class="flex w-full flex-col items-center mt-8 gap-8">
        {#each availableChannels as channel}
          <Button
            class="lg:w-72"
            primary
            disabled={isAuthenticating}
            isBusy={busyChannel === channel}
            on:click={channelMetadata[channel].authFunction}>
            <img src={channelMetadata[channel].icon} class="w-6" alt={channel} slot="icon" />
            {copy[channelMetadata[channel].buttonCopyKey]}
          </Button>
        {/each}
        {#if hasYoutubeError}
          <p class="text-sm text-[--color-red-50]">
            {@html copy["youtubeErrorMessage"]}
          </p>
        {/if}
      </div>
    {/if}
  </svelte:fragment>
</BaseStep>
